/**
 * LAE Productivity (Schools mode): pure number, text and display-date formatters.
 *
 * Ports from the LAE app (`lae-app/src/features/productivity/`):
 * - `format.js`, the number/text half: fmtNum, fmtPct, fmtDuration, fmtShortDuration, talkRows, fmtClock,
 *   fmtDay, fmtDayLong, fmtRange, median, pctChange, splitSchool. The date-arithmetic half (todayIst,
 *   addDays, mondayOf, spanDays, isIsoDate) lives in `lae-dates.ts`; the three display formatters are
 *   kept here so this file imports nothing.
 * - `shared/heroSchools.js` shareOf / withShare (a share of the school's leads, readable when tiny).
 * - `StackedCell.jsx` fmtTalkTotal and titleCase.
 * - `metrics.js` fmtSentiment.
 *
 * Every formatter returns an em dash for null, undefined, '' or a non-finite number, never 0.
 *
 * Deliberate differences from LAE:
 * - talkRows: LAE coerces a missing seconds value to 0 ("0s"). Here a missing value renders as a dash
 *   (null and undefined are "not reported", not zero); the average is a dash with no answered calls.
 * - The per-call average tiles (Avg Call Time and its subsets) are meant to use fmtShortDuration
 *   (seconds shown) rather than LAE's minute-rounded fmtDuration. That choice is made by the hero
 *   builder; both formatters are exported here.
 * - Locale is en-GB throughout (fmtClock keeps LAE's en-IN clock with an upper-cased AM/PM).
 */

const DASH = '—';
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const;
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;
const NUM = new Intl.NumberFormat('en-GB', { maximumFractionDigits: 1 });
const ISO_RE = /^(\d{4})-(\d{2})-(\d{2})/;

/** A value a formatter accepts: upstream numbers, or numeric strings, or nothing. */
export type LaeNumeric = number | string | null | undefined;

function isNum(n: unknown): n is number | string {
  if (n === null || n === undefined || n === '') return false;
  if (typeof n !== 'number' && typeof n !== 'string') return false;
  return Number.isFinite(Number(n));
}

/** 'YYYY-MM-DD' (or a datetime with that prefix) → UTC epoch ms of the calendar day, or null. */
function toUtcMs(iso: unknown): number | null {
  const m = ISO_RE.exec(String(iso ?? ''));
  if (!m) return null;
  const ms = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(ms) ? null : ms;
}

/** Number with en-GB grouping, at most one decimal. */
export function fmtNum(n: LaeNumeric): string {
  return isNum(n) ? NUM.format(Number(n)) : DASH;
}

/** A percentage that is already 0–100 (upstream rates are never fractions) → '34%' / '34.5%'. */
export function fmtPct(n: LaeNumeric, dp = 0): string {
  if (!isNum(n)) return DASH;
  return `${Number(n).toLocaleString('en-GB', { minimumFractionDigits: dp, maximumFractionDigits: dp })}%`;
}

/** Seconds → '2h 05m', '42m', '0m', rounded to the nearest minute. */
export function fmtDuration(sec: LaeNumeric): string {
  if (!isNum(sec)) return DASH;
  const mins = Math.max(0, Math.round(Number(sec) / 60));
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return h > 0 ? `${h}h ${String(m).padStart(2, '0')}m` : `${m}m`;
}

/** Seconds → '3m 05s', '45s'. For averages, which are often under a minute. */
export function fmtShortDuration(sec: LaeNumeric): string {
  if (!isNum(sec)) return DASH;
  const total = Math.max(0, Math.round(Number(sec)));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return m > 0 ? `${m}m ${String(s).padStart(2, '0')}s` : `${s}s`;
}

/** Talk totals: an hour or more reads as hours and minutes, anything shorter keeps its seconds. */
export function fmtTalkTotal(sec: LaeNumeric): string {
  if (!isNum(sec)) return DASH;
  return Number(sec) >= 3600 ? fmtDuration(sec) : fmtShortDuration(sec);
}

/** −1..1 → '+0.42', '−0.10' (U+2212), '0.00'. */
export function fmtSentiment(v: LaeNumeric): string {
  if (!isNum(v)) return DASH;
  const n = Math.round(Number(v) * 100) / 100;
  const abs = Math.abs(n).toFixed(2);
  if (n > 0) return `+${abs}`;
  if (n < 0) return `−${abs}`;
  return abs;
}

const clockFormats = new Map<string, Intl.DateTimeFormat>();

/**
 * India-time 'YYYY-MM-DD HH:mm' (what the upstream sends) → '12:11 PM IST', shown in `tz` (an IANA zone)
 * when given. A bad zone falls back to India time.
 */
export function fmtClock(dt: string | null | undefined, tz?: string | null): string {
  const m = /(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2})/.exec(String(dt ?? ''));
  if (!m) return DASH;
  const when = new Date(`${m[1]}T${m[2]}:00+05:30`);
  if (Number.isNaN(when.getTime())) return DASH;
  const zone = tz || 'Asia/Kolkata';
  try {
    let f = clockFormats.get(zone);
    if (!f) {
      f = new Intl.DateTimeFormat('en-IN', {
        timeZone: zone, hour: 'numeric', minute: '2-digit', hour12: true, timeZoneName: 'short',
      });
      clockFormats.set(zone, f);
    }
    return f.format(when).replace(/ (am|pm) /i, (x) => x.toUpperCase());
  } catch {
    return zone === 'Asia/Kolkata' ? DASH : fmtClock(dt);
  }
}

/** '2026-10-15' (or a datetime) → '15 Oct'. */
export function fmtDay(iso: string | null | undefined): string {
  const ms = toUtcMs(iso);
  if (ms === null) return DASH;
  const d = new Date(ms);
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()] ?? ''}`;
}

/** '2026-10-15' → 'Thu 15 Oct 2026'. */
export function fmtDayLong(iso: string | null | undefined): string {
  const ms = toUtcMs(iso);
  if (ms === null) return DASH;
  const d = new Date(ms);
  return `${WEEKDAYS[d.getUTCDay()] ?? ''} ${d.getUTCDate()} ${MONTHS[d.getUTCMonth()] ?? ''} ${d.getUTCFullYear()}`;
}

/** → '15 Oct 2026', '1–15 Oct 2026', '28 Sep – 15 Oct 2026', '28 Dec 2025 – 3 Jan 2026'. Order-insensitive. */
export function fmtRange(from: string | null | undefined, to: string | null | undefined): string {
  const a = toUtcMs(from);
  const b = toUtcMs(to);
  if (a === null || b === null) return DASH;
  const x = new Date(Math.min(a, b));
  const y = new Date(Math.max(a, b));
  const xd = x.getUTCDate();
  const xm = MONTHS[x.getUTCMonth()] ?? '';
  const xy = x.getUTCFullYear();
  const yd = y.getUTCDate();
  const ym = MONTHS[y.getUTCMonth()] ?? '';
  const yy = y.getUTCFullYear();
  if (a === b) return `${yd} ${ym} ${yy}`;
  if (xy === yy && xm === ym) return `${xd}–${yd} ${ym} ${yy}`;
  if (xy === yy) return `${xd} ${xm} – ${yd} ${ym} ${yy}`;
  return `${xd} ${xm} ${xy} – ${yd} ${ym} ${yy}`;
}

/** Percent change of `cur` against `prev`; null when either is missing or `prev` is 0. */
export function pctChange(cur: LaeNumeric, prev: LaeNumeric): number | null {
  if (!isNum(cur) || !isNum(prev) || Number(prev) === 0) return null;
  return ((Number(cur) - Number(prev)) / Number(prev)) * 100;
}

/** Median of the numeric entries; null / undefined / non-finite are ignored; null when none remain. */
export function median(nums: readonly LaeNumeric[] | null | undefined): number | null {
  const v = (nums ?? []).filter(isNum).map(Number).sort((p, q) => p - q);
  if (v.length === 0) return null;
  const mid = Math.floor(v.length / 2);
  const hi = v[mid] as number;
  return v.length % 2 ? hi : ((v[mid - 1] as number) + hi) / 2;
}

/** 'Emory University | Goizueta' → { primary: 'Emory University', secondary: 'Goizueta' }. */
export function splitSchool(name: string | null | undefined): { primary: string; secondary: string } {
  const s = String(name ?? '');
  const i = s.indexOf(' | ');
  if (i < 0) return { primary: s, secondary: '' };
  return { primary: s.slice(0, i), secondary: s.slice(i + 3) };
}

/**
 * `n` as a share of `of` (the school's leads), readable when tiny: '0.07%', '<0.01%', '12.5%', '40%'.
 * null when `n` is missing or `of` is missing or 0.
 */
export function shareOf(n: LaeNumeric, of: LaeNumeric): string | null {
  if (!isNum(n) || !isNum(of) || Number(of) === 0) return null;
  const p = (Number(n) / Number(of)) * 100;
  if (!p) return '0%';
  if (p < 0.01) return '<0.01%';
  return `${p < 1 ? p.toFixed(2) : p.toFixed(1).replace(/\.0$/, '')}%`;
}

/** '1,204 (34%)' when a share can be worked out, '1,204' when it cannot, a dash when `n` is missing. */
export function withShare(n: LaeNumeric, of: LaeNumeric): string {
  if (!isNum(n)) return DASH;
  const share = shareOf(n, of);
  return share === null ? fmtNum(n) : `${fmtNum(n)} (${share})`;
}

/** 'school_visit' / 'in-progress' → 'School Visit' / 'In Progress'. */
export function titleCase(s: string | null | undefined): string {
  return String(s ?? '')
    .replace(/[_-]+/g, ' ')
    .trim()
    .replace(/\b[a-z]/g, (c) => c.toUpperCase());
}

/** One chart hover row: a name and an already formatted value. */
export interface LaeTalkRow {
  label: string;
  value: string;
}

/**
 * Chart hover rows for one bucket: total talk time and talk per answered call. `suffix` names the subset
 * in any case, e.g. 'positive changes' → 'Total Call Time - Positive Changes'.
 */
export function talkRows<P extends object>(
  point: P | null | undefined,
  secondsKey: keyof P & string,
  callsKey: keyof P & string,
  suffix?: string | null,
): LaeTalkRow[] {
  const rawSec: unknown = point ? point[secondsKey] : undefined;
  const rawCalls: unknown = point ? point[callsKey] : undefined;
  const sec = isNum(rawSec) ? Number(rawSec) : null;
  const calls = isNum(rawCalls) ? Number(rawCalls) : null;
  const tail = suffix ? ` - ${titleCase(suffix)}` : '';
  return [
    { label: `Total Call Time${tail}`, value: fmtTalkTotal(sec) },
    { label: `Avg Call Time${tail}`, value: sec !== null && calls !== null && calls > 0 ? fmtShortDuration(sec / calls) : DASH },
  ];
}
